/**
 * User Profile API client — domain-specific functions for user profile,
 * notification preferences, and connected accounts management.
 *
 * Requirements: 14.1, 14.2, 10.1, 10.2, 12.6
 */

import * as api from '../services/api-client';

// ---------------------------------------------------------------------------
// Types — User Profile
// ---------------------------------------------------------------------------

export interface UserProfileData {
  readonly userId: string;
  readonly displayName: string;
  readonly email: string;
  readonly avatarUrl: string | null;
  readonly createdAt: string;
}

export interface UpdateProfileRequest {
  readonly displayName?: string;
  readonly avatarUrl?: string | null;
}

// ---------------------------------------------------------------------------
// Types — Notification Preferences
// ---------------------------------------------------------------------------

export interface ChannelToggles {
  readonly inApp: boolean;
  readonly email: boolean;
  readonly teams: boolean;
  readonly slack: boolean;
}

export interface NotificationPreferences {
  readonly 'card.assigned': ChannelToggles;
  readonly 'board.invite.created': ChannelToggles;
  readonly 'comment.mention': ChannelToggles;
  readonly 'card.due.approaching': ChannelToggles;
  readonly 'card.moved': ChannelToggles;
}

// ---------------------------------------------------------------------------
// Types — Connected Accounts
// ---------------------------------------------------------------------------

export type ConnectedAccountStatus = 'connected' | 'disconnected' | 'requires_reauth';

export type ConnectedAccountProvider = 'microsoft' | 'google' | 'slack';

export interface ConnectedAccount {
  readonly provider: ConnectedAccountProvider;
  readonly status: ConnectedAccountStatus;
  readonly configuredAt: string;
  readonly metadata: Record<string, unknown>;
}

export interface ConnectAccountRequest {
  readonly tokens?: Record<string, string>;
  readonly webhookUrl?: string;
  readonly teamId?: string;
  readonly channelId?: string;
}

// ---------------------------------------------------------------------------
// API Functions — User Profile
// ---------------------------------------------------------------------------

/**
 * Fetches the authenticated user's profile.
 */
export async function getProfile(): Promise<UserProfileData> {
  return api.get<UserProfileData>('/v1/users/me');
}

/**
 * Updates the authenticated user's profile (displayName, avatarUrl).
 *
 * @param data - Fields to update
 */
export async function updateProfile(data: UpdateProfileRequest): Promise<UserProfileData> {
  return api.put<UserProfileData>('/v1/users/me', data);
}

// ---------------------------------------------------------------------------
// API Functions — Notification Preferences
// ---------------------------------------------------------------------------

/**
 * Fetches the authenticated user's notification preferences.
 */
export async function getPreferences(): Promise<NotificationPreferences> {
  return api.get<NotificationPreferences>('/v1/users/me/notification-preferences');
}

/**
 * Replaces the authenticated user's notification preferences.
 *
 * @param prefs - Full preferences object to persist
 */
export async function updatePreferences(prefs: NotificationPreferences): Promise<NotificationPreferences> {
  return api.put<NotificationPreferences>('/v1/users/me/notification-preferences', prefs);
}

// ---------------------------------------------------------------------------
// API Functions — Connected Accounts
// ---------------------------------------------------------------------------

/**
 * Fetches all connected accounts for the authenticated user.
 */
export async function getConnectedAccounts(): Promise<ConnectedAccount[]> {
  return api.get<ConnectedAccount[]>('/v1/users/me/connected-accounts');
}

/**
 * Connects an external account for the authenticated user.
 *
 * @param provider - The integration provider (microsoft, google, slack)
 * @param data - Connection data (OAuth tokens or webhook URL)
 */
export async function connectAccount(
  provider: ConnectedAccountProvider,
  data: ConnectAccountRequest
): Promise<ConnectedAccount> {
  return api.put<ConnectedAccount>(
    `/v1/users/me/connected-accounts/${encodeURIComponent(provider)}`,
    data
  );
}

/**
 * Disconnects an external account for the authenticated user.
 *
 * @param provider - The integration provider to disconnect
 */
export async function disconnectAccount(
  provider: ConnectedAccountProvider
): Promise<void> {
  await api.del(`/v1/users/me/connected-accounts/${encodeURIComponent(provider)}`);
}

// ---------------------------------------------------------------------------
// Types — MCP Keys
// ---------------------------------------------------------------------------

export interface McpKeyListItem {
  readonly keyId: string;
  readonly name: string;
  readonly prefix: string;
  readonly createdAt: string;
}

export interface McpKeyCreateResponse {
  readonly keyId: string;
  readonly name: string;
  readonly key: string;
  readonly prefix: string;
  readonly createdAt: string;
}

// ---------------------------------------------------------------------------
// API Functions — MCP Keys
// ---------------------------------------------------------------------------

/**
 * Creates a new personal MCP API key.
 * The raw key is only returned in this response — store it securely.
 */
export async function createMcpKey(name: string): Promise<McpKeyCreateResponse> {
  return api.post<McpKeyCreateResponse>('/v1/users/me/mcp-keys', { name });
}

/**
 * Lists the current user's MCP keys (prefix + name + date, never the full key).
 */
export async function listMcpKeys(): Promise<{ data: McpKeyListItem[] }> {
  return api.get<{ data: McpKeyListItem[] }>('/v1/users/me/mcp-keys');
}

/**
 * Revokes (deletes) an MCP key by its ID.
 */
export async function revokeMcpKey(keyId: string): Promise<void> {
  await api.del(`/v1/users/me/mcp-keys/${encodeURIComponent(keyId)}`);
}
